import React, { useId, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * One show/hide item (the reference's FAQ row): a full-width `<button>` with
 * `aria-expanded` + `aria-controls`, a rust "+" / "−" marker, and the panel below it
 * (removed from the accessibility tree and hidden with `hidden` while closed).
 * Keyboard: Tab to the button, Enter or Space toggles. No animation.
 *
 * Props:
 * - `summary` (required): the button's text (the question)
 * - `children`: the panel content (the answer)
 * - `defaultOpen`: initial state when uncontrolled (default false)
 * - `open` + `onOpenChange(next)`: controlled use (`onOpenChange` also fires uncontrolled)
 * - `id`: base id → `<id>-button` / `<id>-panel` (default from useId)
 * - `headingLevel`: 2–6 wraps the button in that heading (the APG accordion pattern),
 *   for when the questions belong in the page outline; default: no heading
 * - `className` (the row; it carries data-state="open|closed" for styling, e.g.
 *   `data-[state=open]:bg-brand-sheet/60`), `buttonClassName`, `panelClassName`
 */
export default function Disclosure({
  summary,
  children,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  id,
  headingLevel,
  className,
  buttonClassName,
  panelClassName,
}) {
  const autoId = useId();
  const base = id || `site-disclosure-${autoId}`;
  const buttonId = `${base}-button`;
  const panelId = `${base}-panel`;
  const [inner, setInner] = useState(Boolean(defaultOpen));
  const controlled = openProp !== undefined;
  const open = controlled ? Boolean(openProp) : inner;

  const toggle = () => {
    const next = !open;
    if (!controlled) setInner(next);
    if (onOpenChange) onOpenChange(next);
  };

  const button = (
    <button
      type="button"
      id={buttonId}
      aria-expanded={open ? 'true' : 'false'}
      aria-controls={panelId}
      onClick={toggle}
      className={cn(
        'flex min-h-11 w-full items-baseline justify-between gap-6 bg-transparent py-1.5 text-left font-body text-base font-medium text-navy',
        buttonClassName,
      )}
    >
      <span className="min-w-0">{summary}</span>
      <span aria-hidden="true" className="shrink-0 font-label text-xl leading-none text-brand-rust">
        {open ? '−' : '+'}
      </span>
    </button>
  );

  const level = [2, 3, 4, 5, 6].includes(Number(headingLevel)) ? Number(headingLevel) : null;
  const HeadingTag = level ? `h${level}` : null;

  return (
    <div
      data-state={open ? 'open' : 'closed'}
      className={cn('border-b border-dashed border-brand-muted/40 py-[1.1875rem]', className)}
    >
      {HeadingTag ? <HeadingTag className="m-0 font-body text-base font-medium">{button}</HeadingTag> : button}
      <div
        id={panelId}
        hidden={!open}
        className={cn('pr-[1.375rem] pt-[0.9375rem] text-sm leading-relaxed text-brand-muted nav:text-base', panelClassName)}
      >
        {children}
      </div>
    </div>
  );
}
